/**
 * @fileoverview Loads states, towns, and buyer companies for the buyer company form.
 * @module hooks/useBuyerCompanyReferenceData
 */
import { useState, useEffect, useCallback } from "react";
import { logger, sanitizeError } from "../utils/logger.js";
import { getReferenceData } from "../logic/companyEdit";
import { getBuyerCompanies } from "../logic/buyerCompany/index.js";

const useBuyerCompanyReferenceData = (navigate) => {
  const [states, setStates] = useState([]);
  const [towns, setTowns] = useState([]);
  const [filteredTowns, setFilteredTowns] = useState([]);
  const [existingCompanies, setExistingCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReferenceData = useCallback(async () => {
    try {
      logger.info("Fetching reference data...");
      setLoading(true);
      const [statesResult, townsResult, existingCompaniesResult] =
        await Promise.all([
          getReferenceData("states", navigate),
          getReferenceData("towns", navigate),
          getBuyerCompanies(navigate),
        ]);

      logger.info("Reference data fetched successfully");
      logger.info("States:", { data: statesResult });
      logger.info("Towns:", { data: townsResult });
      logger.info("Existing Companies:", { data: existingCompaniesResult });

      setStates(statesResult && statesResult.length > 0 ? statesResult : []);
      setTowns(townsResult && townsResult.length > 0 ? townsResult : []);
      setFilteredTowns(
        townsResult && townsResult.length > 0 ? townsResult : [],
      );
      setExistingCompanies(
        existingCompaniesResult && existingCompaniesResult.length > 0
          ? existingCompaniesResult
          : [],
      );
    } catch (referenceDataError) {
      logger.error("Error fetching reference data:", {
        err: sanitizeError(referenceDataError),
      });
      setStates([]);
      setTowns([]);
      setFilteredTowns([]);
      setExistingCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  return {
    states,
    towns,
    filteredTowns,
    setFilteredTowns,
    existingCompanies,
    loading,
    setLoading,
  };
};

export default useBuyerCompanyReferenceData;
